import { useMutation, useQuery } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import React, { createContext, useState, ReactNode, useEffect } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";

import { PLATFORMS_INVALIDATE_KEYS } from "@/constants";
import { useCurrentOrganization, useSwitchPlatform } from "@/contexts";
import {
  refetchGetAllPlatformNamesQuery,
  refetchGetOrganizationPlatformByIdQuery,
  refetchGetPlatformsQuery,
  useGetOrganizationPlatformByIdLazyQuery,
} from "@/graphql";
import { Language, useT } from "@/i18n";
import { mapToDateFormat, mapToTimeFormat } from "@/mappers";
import { mapToThirdPartyLicense } from "@/mappers/license.mappers";
import { mapToApiOrganizationType, mapToOrganizationType } from "@/mappers/organization.mappers";
import { mapPlatformLanguageToLanguage, parseStringifiedArray } from "@/mappers/platform.mappers";
import type { OrganizationReference, OrganizationType } from "@/types";
import type { PlatformSetting } from "@/types/platform.types";
import type { ThirdPartyLicense } from "@/types/user.types";
import countries from "src/components/dashboard/account/countries";
import { EntryFeeData, LicenseFeeData, rowPerPage } from "src/constants";
import CREATEPLATFORM from "src/graphql/mutations/createPlatform";
import UPDATE_PLATEFORM from "src/graphql/mutations/updatePlatform";
import { GET_TEMPLATE } from "src/graphql/queries";

const initialPlatformObject = {
  email: "",
  organizationName: "",
  organizationType: "" as OrganizationType | "",
  organizationAbbreviation: "",
  contactEmail: "",
  contactPhone: "",
  website: "",
  logo: "",
  title: "",
  abbreviation: "",
  platformType: "",
  status: "active",
  country: [] as string[],
  language: [] as Language[],
  timeZone: "",
  currency: [] as string[],
  description: "",
  alreadyDomain: false,
  platformLink: "",
  subDomain: "",
  domain: "",
  companyName: "",
  registrationNumber: "",
  fullAddress: "",
  companyEmail: "",
  needLanding: false,
  needIntegration: false,
  onlinePayment: false,
  licenseMemberships: false,
  onlinePayments: false,
  onlineBookingCourts: false,
  liveStreaming: false,
  liveScoring: false,
  serviceProvider: "",
  signeeFullName: "",
  signeeRole: "",
  primaryColor: "",
  secondaryColor: "",
  darkLogo: "",
  lightLogo: "",
  landingLink: "",
  isEnableMaintenance: false,
  isMobileAppSupported: false,
  passcode: "",
  maintenanceDescription: "",
  maintenanceTitle: "",
  coverPhoto: "",
  yourRequest: "",
  uploadFile: "",
  instagram: "",
  role: "federation",
  password: "",
  federationId: "",
  federationCheck: "",
  name: "",
  surname: "",
  about: "",
  tournatedEntryFee: 5,
  partnerEntryFee: 1,
  tournatedLicenseFee: 5,
  partnerLicenseFee: 1,
  entryFeeType: "FIXED",
  dateFormat: "european",
  timeFormat: "h24",
  platformSettingId: null as number | null,
  isTournatedFeeVatEnable: false,
  tournatedFeeVat: 0,
  liveStreaming_sporfie_key: "",
  platformSetting: null as PlatformSetting | null,
  thirdPartyLicenses: [] as ThirdPartyLicense[],
  accessCode: "",
};

const PlatformContext = createContext<any>({
  platformObject: initialPlatformObject,
  updatePlatformObject: () => {},
  registerPlatform: async () => {},
  updatePlatform: async () => {},
  isLoading: false,
  ownerOrganization: null,
  setOwnerOrganization: () => {},
  logo: "",
  setLogo: () => {},
  setDarkLogo: () => {},
  setOrganizationPlatformId: () => {},
  darkLogo: "",
  setLightLogo: () => {},
  setLandingLink: () => {},
  setIsEnableMaintenance: () => {},
  setIsMobileAppSupported: () => {},
  setPasscode: () => {},
  setMaintenanceDescription: () => {},
  setMaintenanceTitle: () => {},
  setSelectedSport: () => {},
  lightLogo: "",
  landingLink: "",
  isEnableMaintenance: false,
  isMobileAppSupported: false,
  passcode: "",
  maintenanceDescription: "",
  maintenanceTitle: "",
  setCover: () => {},
  cover: "",
  setUploadFiles: () => {},
  uploadFiles: "",
  setPrimary: () => {},
  setIsEnablePrice: () => {},
  isEnablePrice: false,
  primary: "",
  setSecondary: () => {},
  secondary: "",
  setNeedLanding: () => {},
  setFederationId: () => {},
  setFederationCheck: () => {},
  federationId: "",
  needLanding: "",
  updateLoader: false,
  isPlatformLoading: false,
  selectedSport: [],
  federationCheck: "",
  thirdPartyLicenses: [],
});

type PlatformProviderProps = {
  children: ReactNode;
  id: string;
};

export const PlatformProvider: React.FC<PlatformProviderProps> = ({ children, id }) => {
  const navigate = useNavigate();
  const { data: Template } = useQuery(GET_TEMPLATE);
  const switchPlatform = useSwitchPlatform();
  const currentOrganization = useCurrentOrganization();
  // Organization that will own the new platform (create flow). Defaults to the
  // scoped organization and is settable from the "Organization owner" step. Sent
  // to the API as `federationPublicId` so the platform attaches to it.
  const [ownerOrganization, setOwnerOrganization] = useState<OrganizationReference | null>(null);
  const [platformObject, setPlatformObject] =
    useState<typeof initialPlatformObject>(initialPlatformObject);
  const [isLoading, setIsLoading] = useState(false);
  const [updateLoader, setUpdateLoader] = useState(false);
  const [isPlatformLoading, setIsPlatformloading] = useState(false);
  const [logo, setLogo] = useState<any[]>([]);
  const [darkLogo, setDarkLogo] = useState<any[]>([]);
  const [lightLogo, setLightLogo] = useState<any[]>([]);
  const [lightEmblem, setLightEmblem] = useState<any[]>([]);
  const [darkEmblem, setDarkEmblem] = useState<any[]>([]);
  const [favicon, setFavicon] = useState<any[]>([]);

  const [organizationPlatformId, setOrganizationPlatformId] = useState<any>("");

  const [landingLink, setLandingLink] = useState("");
  const [isEnableMaintenance, setIsEnableMaintenance] = useState(false);
  const [isMobileAppSupported, setIsMobileAppSupported] = useState(false);

  const [passcode, setPasscode] = useState("Tournated!123");
  const [maintenanceDescription, setMaintenanceDescription] = useState(
    "Appologies for the inconvenience"
  );
  const [maintenanceTitle, setMaintenanceTitle] = useState("Platform is under maintenance");

  const [selectedSport, setSelectedSport] = useState<any[]>([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState<any[]>([]);
  const [selectedLicense, setSelectedLicense] = useState<any[]>([]);
  const [thirdPartyLicenses, setThirdPartyLicenses] = useState<ThirdPartyLicense[]>([]);
  const [isAlreadyCreatedEntry, setIsAlreadyCreatedEntry] = useState(false);
  const [isAlreadyCreatedLicense, setIsAlreadyCreatedLicense] = useState(false);

  const [cover, setCover] = useState<any[]>([]);
  const [uploadFiles, setUploadFiles] = useState<any[]>([]);
  const [primary, setPrimary] = useState("#ff0000");
  const [isEnablePrice, setIsEnablePrice] = useState(false);
  const [secondary, setSecondary] = useState("#ff0000");
  const [needLanding, setNeedLanding] = React.useState(true);
  const [federationId, setFederationId] = useState<any>();
  const [federationCheck, setFederationCheck] = useState<any>(false);
  const [mobileApp, setMobileApp] = useState(false);
  const [smm, setSmm] = useState(false);
  const [graphicsForSmm, setGraphicsForSmm] = useState(false);
  const [templateId, setTemplateId] = useState<any>(1);
  const [temeplate, setTemplate] = useState<any>([]);
  const [entryFees, setEntryFees] = useState(EntryFeeData);
  const [licenseFee, setLicenseFee] = useState(LicenseFeeData);
  const [serviceFee, setServiceFee] = useState({
    ServiceFeeType: "PERCENTAGE",
    entryFeeCurrency: "EUR",
    isEntryMonotized: false,
    isManagerPayingServiceFee: false,
    isTournatedFeeVatEnable: false,
    tournatedFeeVat: 0,
    feeMode: "PER_ENTRY",
  });
  const [licenseServiceFee, setLicenseServiceFee] = useState({
    licenseFeeType: "PERCENTAGE",
    licenseFeeCurrency: "EUR",
    isLicenseMonotized: false,
    isTournatedFeeVatEnable: false,
    tournatedFeeVat: 0,
    licenseFeeMode: "PER_ENTRY",
  });

  const queryClient = useQueryClient();
  // Legacy platform create/update runs on Apollo; the new REST `PlatformsPage` reads its list from
  // React Query, so invalidate those keys here too or the list goes stale after a legacy edit.
  const invalidatePlatformsList = () =>
    PLATFORMS_INVALIDATE_KEYS.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));

  const [createPlatform, { loading }] = useMutation(CREATEPLATFORM, {
    refetchQueries: [
      refetchGetAllPlatformNamesQuery(),
      // {
      //   query: ORGANIZATION_PLATFORMS,
      //   variables: {
      //     filter: {},
      //   },
      // },
      // refetchGetPlatformsQuery({
      //   page: 0,
      //   limit: rowPerPage,
      //   filter: {},
      // }),
    ],
    awaitRefetchQueries: true,

    onCompleted: (data) => {
      invalidatePlatformsList();
      switchPlatform(data?.createPlatform?.id);
    },
  }) as any;

  const [updatePlatformData, { loading: loader1 }] = useMutation(UPDATE_PLATEFORM, {
    refetchQueries: [
      refetchGetPlatformsQuery({
        page: 0,
        limit: rowPerPage,
        filter: {},
      }),
      refetchGetOrganizationPlatformByIdQuery({
        platformPublicId: id,
      }),
    ],
    onCompleted: () => {
      invalidatePlatformsList();
    },
  }) as any;

  useEffect(() => {
    setIsLoading(loading); // Update context state with GraphQL loading state
  }, [loading]);
  useEffect(() => {
    setUpdateLoader(loader1);
  }, [loader1]);

  const t = useT();

  const updatePlatformObject = (updatedObject: { [key: string]: string | boolean | any }) => {
    setPlatformObject({ ...platformObject, ...updatedObject });
  };
  const updatePlatform = async (requestedData: any) => {
    const variables = {
      ...platformObject,
      platformPublicId: id,
      ...requestedData,
      // @ts-expect-error - File type
      ...(platformObject.uploadFile instanceof File && {
        uploadFile: platformObject.uploadFile,
      }),
      federationEmail: platformObject?.email,
      disciplines: selectedDiscipline.map((item) => item.id),
      thirdPartyLicenses: selectedLicense.map((item) => item.id),
      landingLink,
    };

    if (typeof platformObject.uploadFile === "string") {
      delete variables.uploadFile;
    }
    if (typeof platformObject.darkLogo === "string") {
      delete variables.darkLogo;
    }
    if (typeof platformObject.coverPhoto === "string") {
      delete variables.coverPhoto;
    }
    if (typeof platformObject.lightLogo === "string") {
      delete variables.lightLogo;
    }
    delete variables.instagram;
    delete variables.logo;
    delete variables.federationEmail;
    delete variables.email;
    delete variables.organizationAbbreviation;
    delete variables.organizationName;
    delete variables.organizationType;
    delete variables.website;
    delete variables.contactPhone;
    delete variables.contactEmail;
    // let selecteCountries = variables?.country.map((item) => item?.value);
    const newData = {
      platformPublicId: id,
      ...variables,
      ...(lightLogo[0] instanceof File && {
        lightLogo: lightLogo[0],
      }),
      ...(darkLogo[0] instanceof File && {
        darkLogo: darkLogo[0],
      }),
      ...(lightEmblem[0] instanceof File && {
        lightEmblem: lightEmblem[0],
      }),
      ...(darkEmblem[0] instanceof File && {
        darkEmblem: darkEmblem[0],
      }),
      ...(favicon[0] instanceof File && {
        favicon: favicon[0],
      }),
      ...(uploadFiles[0] instanceof File && {
        uploadFiles: uploadFiles[0],
      }),

      secondaryColor: secondary,
      primaryColor: primary,
      isEnablePrice: isEnablePrice,
      needLanding: needLanding,
      mobileApp: mobileApp,
      isEnableMaintenance,
      isMobileAppSupported,
      passcode,
      maintenanceDescription,
      maintenanceTitle,
      language: JSON.stringify(variables?.language),
      currency: JSON.stringify(variables?.currency),
      country:
        variables?.country?.length === 0
          ? JSON.stringify(countries.map((item) => item?.text))
          : JSON.stringify(variables?.country),

      smm: smm,
      graphicsForSmm: graphicsForSmm,
      template: templateId,
      isTournatedFeeVatEnable:
        requestedData?.isTournatedFeeVatEnable !== undefined
          ? requestedData.isTournatedFeeVatEnable
          : platformObject?.isTournatedFeeVatEnable,
      tournatedFeeVat:
        requestedData?.tournatedFeeVat !== undefined
          ? typeof requestedData.tournatedFeeVat === "string"
            ? parseFloat(requestedData.tournatedFeeVat) || 0
            : requestedData.tournatedFeeVat
          : platformObject?.tournatedFeeVat,
    };
    toast.promise(
      updatePlatformData({
        variables: newData,
      }),
      {
        loading: t("general.loadingState.updating"),
        success: () => {
          Cookies.remove("lang");
          return t("Platform updated");
        },
        error: (err) => {
          return err.message;
        },
      }
    );
  };
  const sportsData = selectedSport?.map((item) => item?.id);
  const registerPlatform = async (values: any) => {
    const variables = {
      ...platformObject,
      ...values,
      role: "federation",
      sports: sportsData,
      phone: platformObject?.contactPhone,
      federation: federationId,
      mobileApp: mobileApp,
      smm: smm,
      graphicsForSmm: graphicsForSmm,
      template: templateId,
      landingLink,
      isEnableMaintenance,
      isMobileAppSupported,
      isEnablePrice,
      passcode,
      maintenanceDescription,
      maintenanceTitle,
      disciplines: selectedDiscipline.map((item) => item.id),
      thirdPartyLicenses: selectedLicense.map((item) => item.id),
      isTournatedFeeVatEnable: platformObject?.isTournatedFeeVatEnable,
      tournatedFeeVat: platformObject?.tournatedFeeVat,

      // @ts-expect-error - File type
      ...(platformObject.uploadFile instanceof File && {
        uploadFile: platformObject.uploadFile,
      }),
      federationEmail: platformObject?.email,
    };

    if (typeof platformObject.uploadFile === "string") {
      delete variables.uploadFile;
    }

    if (federationCheck) {
      delete variables?.email;
      delete variables?.organizationName;
      delete variables?.organizationType;
      delete variables?.organizationAbbreviation;
      delete variables?.contactEmail;
      delete variables?.contactPhone;
      delete variables?.phone;
      delete variables?.logo;
      delete variables?.instagram;
      delete variables?.password;
      delete variables?.role;
    } else {
      delete variables?.federation;
      variables.organizationType = variables.organizationType
        ? mapToApiOrganizationType(variables.organizationType as OrganizationType)
        : null;
    }

    // The new platform is owned by the selected organization (defaults to the
    // scoped one). Sent as `federationPublicId` so the API attaches it to that
    // existing organization instead of trying to create/lookup one from empty
    // fields — omitting it is what produced the "reading 'id' of null" crash.
    const ownerPublicId = ownerOrganization?.id ?? currentOrganization?.id;
    if (!ownerPublicId) {
      toast.error(t("platform.entityAccount.form.owner.error.required"));
      return;
    }
    variables.federationPublicId = ownerPublicId;

    // Upload-typed fields must only be sent when they hold an actual File —
    // the GraphQL `Upload` scalar rejects empty strings / plain objects with
    // "Upload value invalid." Drop any that aren't a File so the mutation
    // omits them instead of sending an invalid placeholder.
    const uploadFields = [
      "logo",
      "darkLogo",
      "lightLogo",
      "lightEmblem",
      "darkEmblem",
      "favicon",
      "uploadFile",
    ] as const;
    for (const field of uploadFields) {
      if (!(variables[field] instanceof File)) {
        delete variables[field];
      }
    }

    toast.promise(
      createPlatform({
        variables: variables,
      }),
      {
        loading: t("platform.loadingState.creatingPlatform"),
        success: () => {
          navigate("/dashboard/platform-members");
          return t("Platform created successfully");
        },
        error: (err) => {
          return err.message;
        },
      }
    );
  };

  const [getPlatform, { loading: isLoader }] = useGetOrganizationPlatformByIdLazyQuery({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    setIsPlatformloading(isLoader);
  }, [isLoader]);

  const getPlatformData = async (id: string) => {
    const variables = {
      platformPublicId: id,
    };
    try {
      const { data } = await getPlatform({ variables });
      setOrganizationPlatformId(data?.organizationPlatformByFields?.id);
      const { platform, federation } = data?.organizationPlatformByFields ?? {};
      setFederationId(federation?.id);
      let countryData: string[] = [];
      const countriesData = platform?.country;

      if (typeof platform?.country === "string" && countriesData && countriesData.length > 0) {
        const newArray = countries.filter(
          (country) => countriesData.includes(country.value) || countriesData.includes(country.text)
        );

        countryData = newArray.map((item) => item.text);
      }

      const stringData = platform?.domain;
      setPlatformObject({
        ...platformObject,
        email: federation?.user?.email ?? "",
        organizationName: federation?.organizationName ?? "",
        organizationAbbreviation: federation?.organizationAbbreviation ?? "",
        organizationType: federation?.organizationType
          ? mapToOrganizationType(federation.organizationType)
          : "",
        contactEmail: federation?.contactEmail ?? "",
        contactPhone: federation?.contactPhone ?? "",
        website: federation?.website ?? "",
        about: federation?.about ?? "",
        name: federation?.user?.name ?? "",
        surname: federation?.user?.surname ?? "",
        instagram: federation?.user?.instagram ?? "",
        title: platform?.title ?? "",
        platformType: platform?.platformType ?? "",
        status: platform?.status ?? "",

        country: countryData ?? [],

        language: parseStringifiedArray(platform?.language).map(mapPlatformLanguageToLanguage),
        timeZone: platform?.timeZone ?? "",
        currency: parseStringifiedArray(platform?.currency),
        description: platform?.description ?? "",
        alreadyDomain: platform?.alreadyDomain ?? false,
        platformLink: platform?.platformLink ?? "",
        subDomain: platform?.subDomain ?? "",
        domain: stringData ?? "",
        needIntegration: platform?.needIntegration ?? false,
        companyName: platform?.companyName ?? "",
        registrationNumber: platform?.registrationNumber ?? "",
        fullAddress: platform?.fullAddress ?? "",
        companyEmail: platform?.companyEmail ?? "",
        licenseMemberships: platform?.licenseMemberships ?? false,
        serviceProvider: platform?.serviceProvider ?? "",
        onlinePayments: platform?.onlinePayments ?? false,
        yourRequest: platform?.yourRequest ?? "",
        signeeRole: platform?.signeeRole ?? "",
        signeeFullName: platform?.signeeFullName ?? "",
        tournatedEntryFee: platform?.tournatedEntryFee ?? 0,
        partnerEntryFee: platform?.partnerEntryFee ?? 0,
        tournatedLicenseFee: platform?.tournatedLicenseFee ?? 0,
        partnerLicenseFee: platform?.partnerLicenseFee ?? 0,
        abbreviation: platform?.abbreviation ?? "",
        liveStreaming_sporfie_key: platform?.liveStreaming_sporfie_key ?? "",
        accessCode: platform?.accessCode ?? "",
        platformSettingId: platform?.platformSetting?.id ?? null,
        dateFormat: platform?.platformSetting?.dateFormatPreference
          ? mapToDateFormat(platform.platformSetting.dateFormatPreference)
          : "european",
        timeFormat: platform?.platformSetting?.timeFormatPreference
          ? mapToTimeFormat(platform.platformSetting.timeFormatPreference)
          : "h24",

        isTournatedFeeVatEnable: platform?.isTournatedFeeVatEnable ?? false,
        tournatedFeeVat: platform?.tournatedFeeVat ?? 0,
        thirdPartyLicenses: platform?.thirdPartyLicenses?.map(mapToThirdPartyLicense) ?? [],
      });
      setSelectedSport(
        platform?.sports?.map((item) => {
          return {
            name: item?.sport!.title,
            id: item?.sport?.id,
            platformSportId: item?.id,
          };
        }) ?? []
      );
      setTemplateId(platform?.template?.id);
      setPrimary(platform?.primaryColor ?? "");
      setIsEnablePrice(platform?.isEnablePrice ?? false);
      setSecondary(platform?.secondaryColor ?? "");
      setNeedLanding(platform?.needLanding ?? false);
      setSmm(platform?.smm ?? false);
      setGraphicsForSmm(platform?.graphicsForSmm ?? false);
      setMobileApp(platform?.mobileApp ?? false);
      setLandingLink(platform?.landingLink ?? "");
      setIsEnableMaintenance(platform?.isEnableMaintenance ?? false);
      setIsMobileAppSupported(platform?.isMobileAppSupported ?? false);
      if (platform?.passcode) {
        setPasscode(platform?.passcode);
      }
      if (platform?.maintenanceDescription) {
        setMaintenanceDescription(platform?.maintenanceDescription);
      }
      if (platform?.maintenanceTitle) {
        setMaintenanceTitle(platform?.maintenanceTitle);
      }

      setSelectedDiscipline(
        platform?.disciplines?.map((item) => {
          return {
            id: item?.id,
            name: item?.title,
          };
        }) ?? []
      );
      setSelectedLicense(
        platform?.thirdPartyLicenses?.map((item) => {
          return {
            id: item?.id,
            name: item?.title,
          };
        }) ?? []
      );
      setThirdPartyLicenses(platform?.thirdPartyLicenses?.map(mapToThirdPartyLicense) ?? []);
      const entryFee: any[] = [];
      const licenseFee: any[] = [];
      if (platform?.customFee) {
        for (let i = 0; i <= platform!.customFee!.length; i++) {
          if (platform!.customFee![i]?.serviceType === "ENTRY") {
            setServiceFee({
              ...serviceFee,
              ServiceFeeType: platform?.customFee[i]?.feeType,
              entryFeeCurrency: platform?.customFee[i]?.currency ?? "",
              isEntryMonotized: platform?.customFee[i]?.isMonetization ?? false,
              isManagerPayingServiceFee: platform?.customFee[i]?.isManagerPayingServiceFee ?? false,
              isTournatedFeeVatEnable: platform?.isTournatedFeeVatEnable ?? false,
              tournatedFeeVat: platform?.tournatedFeeVat ?? 0,
              feeMode: platform?.customFee[i]?.feeMode || "PER_ENTRY",
            });
            const data = {
              ...(platform?.customFee[i]?.feeType == "FIXED" && {
                tournatedFixedEntryFee: platform?.customFee[i]?.tournatedFee,
                partnerFixedEntryFee: platform?.customFee[i]?.partnerFee,
              }),
              ...(platform?.customFee[i]?.feeType == "PERCENTAGE" && {
                tournatedEntryFee: platform?.customFee[i]?.tournatedFee,
                partnerEntryFee: platform?.customFee[i]?.partnerFee,
              }),

              rangeStart: platform?.customFee[i]?.rangeStart,
              rangeEnd: platform?.customFee[i]?.rangeEnd,
              minFee: platform?.customFee[i]?.tournatedMinFee,
              maxFee: platform?.customFee[i]?.tournatedMaxFee,
              partnerMin: platform?.customFee[i]?.partnerMinFee,
              partnerMax: platform?.customFee[i]?.partnerMaxFee,
              tournatedMinPercentageFee: platform?.customFee[i]?.tournatedMinPercentageFee,
              tournatedMaxPercentageFee: platform?.customFee[i]?.tournatedMaxPercentageFee,
              partnerMinPercentageFee: platform?.customFee[i]?.partnerMinPercentageFee,
              partnerMaxPercentageFee: platform?.customFee[i]?.partnerMaxPercentageFee,
              id: platform?.customFee[i]?.id,
            };
            entryFee.push(data);
          } else if (platform?.customFee[i]?.serviceType === "LICENSE") {
            setLicenseServiceFee({
              ...licenseServiceFee,
              licenseFeeType: platform?.customFee[i]?.feeType,
              licenseFeeCurrency: platform?.customFee[i]?.currency ?? "",
              isLicenseMonotized: platform?.customFee[i]?.isMonetization ?? false,
              isTournatedFeeVatEnable: platform?.isTournatedFeeVatEnable ?? false,
              tournatedFeeVat: platform?.tournatedFeeVat ?? 0,
              licenseFeeMode: platform?.customFee[i]?.feeMode || "PER_ENTRY",
            });
            const data = {
              ...(platform?.customFee[i]?.feeType == "FIXED" && {
                tournatedFixedEntryFee: platform?.customFee[i]?.tournatedFee,
                partnerFixedEntryFee: platform?.customFee[i]?.partnerFee,
              }),
              ...(platform?.customFee[i]?.feeType == "PERCENTAGE" && {
                tournatedEntryFee: platform?.customFee[i]?.tournatedFee,
                partnerEntryFee: platform?.customFee[i]?.partnerFee,
              }),

              rangeStart: platform?.customFee[i]?.rangeStart,
              rangeEnd: platform?.customFee[i]?.rangeEnd,
              minFee: platform?.customFee[i]?.tournatedMinFee,
              maxFee: platform?.customFee[i]?.tournatedMaxFee,
              partnerMin: platform?.customFee[i]?.partnerMinFee,
              partnerMax: platform?.customFee[i]?.partnerMaxFee,
              tournatedMinPercentageFee: platform?.customFee[i]?.tournatedMinPercentageFee,
              tournatedMaxPercentageFee: platform?.customFee[i]?.tournatedMaxPercentageFee,
              partnerMinPercentageFee: platform?.customFee[i]?.partnerMinPercentageFee,
              partnerMaxPercentageFee: platform?.customFee[i]?.partnerMaxPercentageFee,
              id: platform?.customFee[i]?.id,
            };
            licenseFee.push(data);
          }
        }

        if (entryFee?.length > 0) {
          setIsAlreadyCreatedEntry(true);
          setEntryFees(entryFee);
        } else {
          setIsAlreadyCreatedEntry(false);
          setEntryFees(EntryFeeData?.map((entry) => ({ ...entry })));
          setServiceFee({
            ServiceFeeType: "FIXED",
            entryFeeCurrency: "EUR",
            isEntryMonotized: false,
            isManagerPayingServiceFee: false,
            isTournatedFeeVatEnable: platform?.isTournatedFeeVatEnable || false,
            tournatedFeeVat: platform?.tournatedFeeVat || 0,
            feeMode: "PER_ENTRY",
          });
        }
        if (licenseFee?.length > 0) {
          setLicenseFee(licenseFee);
          setIsAlreadyCreatedLicense(true);
        } else {
          setLicenseFee(LicenseFeeData.map((license) => ({ ...license })));

          setLicenseServiceFee({
            licenseFeeType: "FIXED",
            licenseFeeCurrency: "EUR",
            isLicenseMonotized: false,
            isTournatedFeeVatEnable: platform?.isTournatedFeeVatEnable || false,
            tournatedFeeVat: platform?.tournatedFeeVat || 0,
            licenseFeeMode: "PER_ENTRY",
          });
        }
        setDarkLogo([platform?.darkLogo]);
        setLightLogo([platform?.lightLogo]);
        setLightEmblem([platform?.lightEmblem]);
        setDarkEmblem([platform?.darkEmblem]);
        setFavicon([(platform as any)?.favicon]);
        setUploadFiles([platform?.uploadFile]);
        setLogo([federation?.logo]);
        // await handlePreview(platform?.darkLogo, setDarkLogo);
        // await handlePreview(platform?.lightLogo, setLightLogo);
        // await handlePreview(platform?.lightEmblem, setLightEmblem);
        // await handlePreview(platform?.darkEmblem, setDarkEmblem);
        // await handlePreview(platform?.uploadFile, setUploadFiles);
        // await handlePreview(federation?.logo, setLogo);
      }
    } catch (err) {
      console.error(err);
    }
  };
  useEffect(() => {
    if (id) {
      getPlatformData(id);
    }
    // want to run it only when id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return (
    <PlatformContext.Provider
      value={{
        platformObject,
        updatePlatformObject,
        registerPlatform,
        updatePlatform,
        isLoading,
        ownerOrganization,
        setOwnerOrganization,
        setLogo,
        logo,
        setDarkLogo,
        setOrganizationPlatformId,
        organizationPlatformId,
        darkLogo,
        setLightLogo,
        setLightEmblem,
        lightEmblem,
        darkEmblem,
        setDarkEmblem,
        favicon,
        setFavicon,
        setLandingLink,
        setIsEnableMaintenance,
        setIsMobileAppSupported,
        setPasscode,
        setMaintenanceDescription,
        setMaintenanceTitle,
        setSelectedSport,
        lightLogo,
        landingLink,
        isEnableMaintenance,
        isMobileAppSupported,
        passcode,
        maintenanceDescription,
        maintenanceTitle,
        selectedSport,
        setSelectedDiscipline,
        selectedDiscipline,
        setSelectedLicense,
        selectedLicense,
        thirdPartyLicenses,
        setCover,
        cover,
        setUploadFiles,
        uploadFiles,
        setPrimary,
        setIsEnablePrice,

        isEnablePrice,
        primary,
        setSecondary,
        secondary,
        setNeedLanding,
        needLanding,
        updateLoader,
        isPlatformLoading,
        setFederationId,
        setFederationCheck,
        federationId,
        federationCheck,
        setMobileApp,
        mobileApp,
        setSmm,
        smm,
        graphicsForSmm,
        setGraphicsForSmm,
        setTemplateId,
        templateId,
        setTemplate,
        temeplate,
        Template,
        setEntryFees,
        entryFees,
        setLicenseFee,
        licenseFee,
        serviceFee,
        licenseServiceFee,
        setLicenseServiceFee,
        isAlreadyCreatedLicense,
        isAlreadyCreatedEntry,
      }}
    >
      {children}
    </PlatformContext.Provider>
  );
};

export default PlatformContext;
